import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { TableContainer, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  SingleClaimTable,
  CLAIM_MODE_VIEW,
  CLAIM_MODE_EDIT,
  CLAIM_MODE_NEW_CLAIM,
  CLAIM_MODE_NEW_ENTITY,
} from '../components/claim/singleClaimTable';
import { COMPETITION_SCHEMA, COMPETITION_CLAIM_SCHEMA } from '../schema/competitionSchema';
import { ClaimFilters } from '../components/claim/claimFilters';
import ClaimHistoryTable from '../components/table/claimHistoryTable';
import { Progress } from '../components/util/progress';
import {
  GOLDEN_COMPETITION_AND_CLAIMS,
  SAVE_COMPETITION_CLAIM,
  OVERRIDE_COMPETITION_CLAIM,
  INSERT_GOLDEN_COMPETITION,
} from '../hasura/queries/competition/competitionClaims';

import { Error } from '../components/util/notifications';

const useStyle = makeStyles({
  root: {
    marginTop: 20,

    '& h3': {
      paddingLeft: 5,
    },
  },
});
const useStyleButton = makeStyles({
  button: { margin: 5, float: 'right' },
  clearButton: {
    '&:hover': {
      backgroundColor: '#DA0000',
    },
    backgroundColor: '#FF0000',
    color: 'white',
    height: 30,
    padding: 5,
    float: 'right',
    margin: 5,
  },
});

const GoldenCompetitionView = ({ competitionId, isNewCompetition }) => {
  const ignoreEverything = {};
  COMPETITION_SCHEMA.forEach(row => {
    ignoreEverything[row.columnKey] = 'Ignored';
  });

  const [claimFilter, setClaimFilter] = useState('all');
  const [selectedClaimId, setSelectedClaimId] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState(isNewCompetition ? ignoreEverything : {});
  const [overrides, setOverrides] = useState({});
  const [claimMode, setClaimMode] = useState(isNewCompetition ? CLAIM_MODE_NEW_ENTITY : CLAIM_MODE_VIEW);
  const tableContatinerClasses = useStyle();
  const buttonClasses = useStyleButton();
  const { loading, error, data, refetch } = useQuery(GOLDEN_COMPETITION_AND_CLAIMS, {
    variables: {
      competition_id: competitionId,
    },
  });
  if (loading) return <Progress />;
  let allClaims = [];
  data &&
    data.statsbomb_competition &&
    data.statsbomb_competition[0] &&
    data.statsbomb_competition[0].mapping_competitions.forEach(mappingEntry => {
      allClaims = [...allClaims, ...mappingEntry.claim_competitions];
    });

  let filteredClaims = allClaims;
  if (claimFilter === 'undecided') {
    filteredClaims = allClaims.filter(c => {
      return c.approved_at === null && c.rejected_at === null;
    });
  } else if (claimFilter === 'approved') {
    filteredClaims = allClaims.filter(c => c.approved_at != null);
  } else if (claimFilter === 'rejected') {
    filteredClaims = allClaims.filter(c => c.rejected_at != null);
  }
  const selectAClaim = claimUUID => {
    setSelectedClaimId(claimUUID);
    const currentDecisions = {};

    const claim = allClaims.filter(c => {
      return c.claim_competition_uuid === claimUUID;
    })[0];
    if (claim) {
      COMPETITION_SCHEMA.forEach(row => {
        if (claim && claim[row.columnKey] != null) {
          currentDecisions[row.columnKey] =
            row.mapping_function && row.mapping_function(claim) == null ? 'Ignored' : 'Approved';
        }
      });
    }
    setClaimStatuses(currentDecisions);

    const initialOverrides = {};
    COMPETITION_SCHEMA.forEach(row => {
      initialOverrides[row.columnKey] = null;
    });
    setOverrides(initialOverrides);

    setClaimMode(claimUUID ? CLAIM_MODE_EDIT : CLAIM_MODE_VIEW);
  };

  const changeClaimFilter = claimFilter => {
    selectAClaim(null);
    setClaimFilter(claimFilter);
  };

  const newClaim = () => {
    selectAClaim(null);
    setClaimMode(CLAIM_MODE_NEW_CLAIM);
    setClaimStatuses(ignoreEverything);
  };

  const getGoldenCompetitionId = insertedCompetition =>
    insertedCompetition?.data?.insert_statsbomb_competition?.returning?.[0]?.competition_id;

  const claimSelected = claim => {
    selectAClaim(claim.claim_competition_uuid);
  };

  return (
    <>
      <Error message="" error={error} />
      <div className="container-row">
        <h2>Competition Singular: {isNewCompetition ? ' New Competition' : competitionId}</h2>

        {claimMode !== CLAIM_MODE_NEW_ENTITY && (
          <TableContainer component={Paper}>
            <ClaimFilters claimFilter={claimFilter} filterResultsFn={changeClaimFilter} />
            <ClaimHistoryTable
              entityType="competition"
              schema={COMPETITION_CLAIM_SCHEMA}
              entities={filteredClaims}
              identifier={{
                headerValue: 'Competition Name',
                bodyValue: data.statsbomb_competition[0]?.competition_name,
              }}
              rowClickHandler={claimSelected}
              selectedEntityKey={selectedClaimId}
            />
            <Button className={buttonClasses.button} variant="contained" color="primary" onClick={() => newClaim()}>
              New Claim
            </Button>
          </TableContainer>
        )}

        <TableContainer classes={tableContatinerClasses} component={Paper}>
          <h3>Competition Attributes {selectedClaimId && ' And Claim Values'}</h3>
          <SingleClaimTable
            goldenEntity={data.statsbomb_competition[0]}
            claim={
              allClaims.filter(c => {
                return c.claim_competition_uuid === selectedClaimId;
              })[0]
            }
            rowSchema={COMPETITION_SCHEMA}
            saveMutation={SAVE_COMPETITION_CLAIM}
            overrideMutation={OVERRIDE_COMPETITION_CLAIM}
            createMutation={INSERT_GOLDEN_COMPETITION}
            sourcePK="provider_competition_id"
            claimPK="claim_competition_uuid"
            goldenEntityPK="competition_id"
            getGoldenEntityId={getGoldenCompetitionId}
            entityType="competition"
            setClaimModeView={() => setClaimMode(CLAIM_MODE_VIEW)}
            {...{
              claimStatuses,
              setClaimStatuses,
              selectAClaim,
              overrides,
              setOverrides,
              claimMode,
              refetch,
            }}
          />
        </TableContainer>
      </div>
    </>
  );
};

function Competition() {
  const query = new URLSearchParams(useLocation().search);
  const isNewCompetition = query.get('new');
  const competitionId = isNewCompetition ? '0' : query.get('competition_id');

  return <GoldenCompetitionView competitionId={competitionId} isNewCompetition={!!isNewCompetition} />;
}

export default Competition;
